import {useCallback, useEffect, useMemo, type PropsWithChildren} from 'react';
import {clsx} from 'clsx';
import {isContainerQueryAvailable} from '../lib/container-query-with-fallback';
import {useVersion} from '../context';
import {breakPoint} from '../lib/const';
import {useImageListContext} from './context';
import {gapWidth, listWidth, listWidth2015} from './const';
import styles from './list.module.css';

type Props = PropsWithChildren;

export function ImageListList({children}: Props) {
  const {currentSlidePage, setCurrentSlidePage} = useImageListContext();
  const editorVersion = useVersion();

  const is2015Version = useMemo(
    () => editorVersion === '2015',
    [editorVersion],
  );

  const slideContainerWidth = useMemo(
    () => (is2015Version ? listWidth2015 : listWidth) + gapWidth,
    [is2015Version],
  );

  const slidePositionX = useMemo(
    () => slideContainerWidth * (currentSlidePage - 1),
    [currentSlidePage, slideContainerWidth],
  );

  const handleResize = useCallback(() => {
    const isPc = globalThis.matchMedia(`(min-width: ${breakPoint.pc})`).matches;

    if (isPc) {
      setCurrentSlidePage(1);
    }
  }, [setCurrentSlidePage]);

  // PCモードのみ、ユーザーによるスクロール操作を無効にする
  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  return (
    <div
      className={styles.container}
      style={{maxWidth: `${slideContainerWidth}px`}}
    >
      <ul
        className={clsx(
          styles.list,
          is2015Version && styles.list2015,
          isContainerQueryAvailable() ? styles.pressReleaseRoot : styles.screen,
        )}
        style={{transform: `translateX(-${slidePositionX}px)`}}
        data-testid='imageSlider'
      >
        {children}
      </ul>
    </div>
  );
}
